import { createTheme } from '@mui/material/styles';

const ssoTheme = createTheme({
    palette: {
        primary: {
            main: '#231f20',
            light: '#8dc7e9',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#8dc7e9',
            contrastText: '#ffffff',
        },
        error: {
            main: '#e55353',
        },
        warning: {
            main: '#f26407',
        },
        success: {
            main: '#00c300',
        },
        info: {
            main: '#8dc7e9',
        },
        text: {
            primary: '#231f20',
            secondary: '#565a5c',
            disabled: '#a2a4a3',
        },
        background: {
            default: '#ffffff',
            paper: '#ffffff',
        },
        divider: '#e0e0e0',
        action: {
            disabled: '#a2a4a3',
            disabledBackground: '#eceded',
        },
    },
    typography: {
        fontFamily: 'Suisse Intl, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
        fontSize: 14,
        button: {
            textTransform: 'uppercase',
            fontWeight: 800,
            fontSize: '0.875rem',
        },
        body1: {
            fontSize: '0.875rem',
            color: '#231f20',
        },
        body2: {
            fontSize: '0.875rem',
            color: '#565a5c',
        },
    },
    shape: {
        borderRadius: 0,
    },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000000',
                        borderWidth: 1,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.secondary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light,
                        borderWidth: 2,
                    },
                    '&.Mui-focused': {
                        boxShadow: `inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0.5rem ${theme.palette.primary.light}`,
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.error.main,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#d2d4d3',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.text.disabled,
                        },
                    },
                }),
                input: ({ theme }) => ({
                    padding: '0.75rem 1rem',
                    color: theme.palette.text.secondary,
                    '&::placeholder': {
                        color: theme.palette.text.disabled,
                        opacity: 1,
                    },
                }),
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                        padding: '6px 6px',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                    '&.Mui-focused': {
                        color: '#231f20',
                    },
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 0,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    transition: 'background-color 0.1s linear, box-shadow 0.1s linear',
                    '&.Mui-focusVisible': {
                        boxShadow: `0 0 0.5rem ${theme.palette.primary.light}`,
                        outline: 'none',
                    },
                }),
                sizeSmall: { padding: '0.25rem 0.75rem', fontSize: '0.8125rem' },
                sizeMedium: { padding: '0.5rem 1rem', fontSize: '0.875rem' },
                sizeLarge: { padding: '0.75rem 1.5rem', fontSize: '0.9375rem' },
                contained: ({ theme }) => ({
                    backgroundColor: '#231f20',
                    color: '#ffffff',
                    border: '2px solid #231f20',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#ffffff',
                        color: '#231f20',
                        boxShadow: 'none',
                    },
                    '&.Mui-focusVisible': {
                        boxShadow: `0 0 0.5rem ${theme.palette.primary.light}`,
                    },
                }),
                outlined: {
                    backgroundColor: '#ffffff',
                    color: '#231f20',
                    border: '2px solid #231f20',
                    '&:hover': {
                        backgroundColor: '#231f20',
                        color: '#ffffff',
                        border: '2px solid #231f20',
                    },
                },
                text: {
                    color: '#000000',
                    padding: 0,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                        textDecoration: 'underline',
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-focusVisible': {
                        boxShadow: `0 0 0.5rem ${theme.palette.primary.light}`,
                        outline: 'none',
                    },
                }),
                sizeSmall: { padding: '0.25rem', fontSize: '1.125rem' },
                sizeMedium: { padding: '0.5rem', fontSize: '1.5rem' },
                sizeLarge: { padding: '0.75rem', fontSize: '1.75rem' },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                fullWidth: true,
                size: 'small',
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                    border: '1px solid #231f20',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3),
                    paddingBottom: theme.spacing(3),
                    gap: theme.spacing(1),
                }),
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#ffffff',
                    color: '#231f20',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#000000',
                    textDecorationColor: '#000000',
                    '&:hover': {
                        color: '#000000',
                        fontWeight: 700,
                        textDecorationColor: '#000000',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.light}`,
                        outlineOffset: 2,
                        borderRadius: 1,
                    },
                }),
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const sev = ownerState.severity;
                    const palette = sev && theme.palette[sev];
                    return palette ? { border: `1px solid ${palette.main}` } : {};
                },
            },
        },
    },
});

export default ssoTheme;
